/**
 * Deterministic Fisher-Yates shuffle seeded by a string.
 *
 * Same seed → same output, so the MCQ options for a given (wordId, direction)
 * stay stable across re-renders, cache reads, navigation returns, etc.
 *
 * The PRNG matters here. An earlier version used glibc-style
 * `(h * 1103515245 + 12345) & 0x7fffffff` which produced ~2⁶¹-scale products
 * — past the 2⁵³ safe-integer limit for JS doubles. The multiplication got
 * rounded, the low bits went degenerate, and `h % 4` landed on 0 nearly
 * every time. Net effect: in Fisher-Yates on a 4-item array starting with
 * the correct answer at index 0, it swapped into index 3 in ~99.7% of
 * shuffles — the "correct answer is always at the bottom" bug.
 *
 * The fix is `Math.imul` (32-bit integer multiplication with no precision
 * loss) plus a well-distributed PRNG. Using mulberry32 seeded by xmur3 —
 * standard small-but-good pair, passes BigCrush at this scale and only
 * needs a handful of ops per draw.
 */
export function shuffleDeterministic<T>(arr: readonly T[], seed: string): T[] {
  const rand = mulberry32(xmur3(seed));
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/**
 * xmur3: tiny string-to-32-bit-seed hash.
 * Avalanches each input character across the state so similar seeds
 * (e.g. "foo" vs "bar") produce well-separated output seeds.
 */
function xmur3(str: string): number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return h;
}

/**
 * mulberry32: 32-bit PRNG. Returns a draw function that yields doubles in
 * [0, 1). Good statistical properties for our use case (Fisher-Yates on a
 * 4-item array from user-facing IDs). Not cryptographically secure.
 */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
